/**
 * The search app's control params live in the URL, not in component state.
 *
 * `?status_group=All&operator=AND` sits alongside the params Volto's own
 * `withSearch` already mirrors there (`SearchableText`, the facet values,
 * `sort_on`/`sort_order`). That is deliberate: a search is a thing people send
 * to a colleague and paste into a ticket, so every knob that changes the result
 * set has to be in the link. It also means back/forward works for free and SSR
 * renders the same page the link promised.
 *
 * Merging with the existing query string in BOTH directions matters. Core's
 * `useHashState.setSearchData` merges its params into `qs.parse(location.search)`
 * rather than replacing it, so our params survive a facet or sort change; we
 * merge the same way so a facet selection survives a status change.
 *
 * Unknown or misspelled values are dropped rather than forwarded, so a
 * hand-edited URL can't push junk at the seam — an unrecognised status group
 * would make the backend fall back to its default anyway, but then the UI and
 * the URL would disagree about what is applied.
 */
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'query-string';

import {
  STATUS_PARAM,
  STATUS_GROUPS,
  SEARCH_OPERATORS,
} from '../config/searchRegistry.generated';

const VALID_STATUS = new Set(STATUS_GROUPS.map((g) => g.id));
const VALID_OPERATOR = new Set(SEARCH_OPERATORS);

const first = (v) => (Array.isArray(v) ? v[0] : v);

/** Parse a location.search string into the controls the search block accepts. */
export function readSearchControls(search) {
  const q = qs.parse(search || '');
  const status = first(q[STATUS_PARAM]);
  const operator = (first(q.operator) || '').toUpperCase();
  return {
    statusGroup: VALID_STATUS.has(status) ? status : undefined,
    operator: VALID_OPERATOR.has(operator) ? operator : undefined,
    // item 21. Deliberately NOT validated against a vocabulary the way the two
    // above are: its value IS a query string, and the seam only honours it
    // while it matches the text being searched for. An unrecognised value is
    // therefore inert by construction rather than by our filtering it out.
    correct: first(q.correct),
  };
}

/**
 * [controls, setControls] against the URL.
 *
 * `setControls({status_group, operator, ...})` — a value of `undefined`, `''`
 * or `[]` removes the param, which hands the decision back to the backend
 * default (and so re-arms the "applied by default" chip). Params are named
 * exactly as the seam names them, so the patch object is also the request
 * shape. Any other key is written straight through, because Volto mirrors facet
 * values into the query string under their index name — which is what lets the
 * zero-results panel widen the site scope with `{site: [...]}` without reaching
 * into the block's own facet state.
 */
export default function useSearchControls() {
  const location = useLocation();
  const history = useHistory();

  const controls = React.useMemo(
    () => readSearchControls(location.search),
    [location.search],
  );

  const setControls = React.useCallback(
    (patch = {}) => {
      const params = qs.parse(location.search);
      Object.keys(patch).forEach((k) => {
        // `status_group` is the registry's name for the param; keep callers
        // speaking the seam's vocabulary even if the param is ever renamed.
        const key = k === 'status_group' ? STATUS_PARAM : k;
        const value = patch[k];
        const empty =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        if (empty) delete params[key];
        else params[key] = value;
      });
      // pathname omitted on purpose — react-router keeps the current one, and
      // that is exactly how core's own search-state mirror pushes.
      history.push({ search: qs.stringify(params) });
    },
    [history, location.search],
  );

  return [controls, setControls];
}
